import { useContext } from 'react';
import root from 'react-shadow';
import { useParams } from 'react-router';
import { i18n } from 'app/utils/i18n';
import { useCss } from 'app/api/style-queries';
import { useSelectedTemplates } from 'app/api/template-queries';
import { useGetDocument, useGetDocumentTableOfContent } from 'app/api/documents/document-queries';
import { UserContext } from 'app/state/contexts';
import { HoveredTarget, buildHoverModifier } from '../utils/builtin-selectors';
import { AreaConfig, AreaContent, HIGHLIGHT_CSS, renderArea } from './preview-markup';

interface HeaderFooterPreviewProps {
  hoveredInner?: HoveredTarget | null;
}

export function HeaderFooterPreview({ hoveredInner }: HeaderFooterPreviewProps = {}) {
  const { documentId } = useParams<{ documentId: string }>();
  const { selectedOrganization } = useContext(UserContext);
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const { data: selectedTemplates } = useSelectedTemplates(documentId);
  const css = useCss({
    documentSuperId: documentId,
    documentRevisionId: undefined,
    useDraft: true,
    withStyle: undefined,
    embedd: undefined,
  });

  const docContent: any = pDocument.data?.content ?? {};
  const headerConfig: AreaConfig | undefined = docContent.header;
  const footerConfig: AreaConfig | undefined = docContent.footer;
  const metadataTexts: Record<string, Record<string, string>> = docContent.metadata?.texts ?? {};
  const localeKeys = Object.keys(metadataTexts);
  const previewLocale = localeKeys[0];
  const needsHeadings = headerConfig?.type === 'headings' || footerConfig?.type === 'headings';
  const tocQuery = useGetDocumentTableOfContent(needsHeadings ? pDocument.data?.revisionId ?? null : null, { locale: previewLocale });

  if (css.isLoading || !css.data) {
    return (
      <div
        className="header-footer-preview text-muted fs-7 d-flex align-items-center justify-content-center"
        style={{ height: 140, border: '1px dashed #dee2e6', borderRadius: 4 }}
      >
        {i18n('common.loading')}
      </div>
    );
  }

  const templateSuperId = selectedTemplates?.[0] ?? '';
  const headings = (tocQuery.data?.content as Array<{ uuid: string; name: string }> | undefined) ?? undefined;
  const previewContent: AreaContent = {
    texts: previewLocale ? metadataTexts[previewLocale] : undefined,
    headings,
  };

  const headerHoverModifier = buildHoverModifier('area-header', hoveredInner);
  const footerHoverModifier = buildHoverModifier('area-footer', hoveredInner);

  return (
    <div className="header-footer-preview mb-3" style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
      <root.div>
        <style>{css.data.shadowedCss}</style>
        <style>{HIGHLIGHT_CSS}</style>
        <style>{`
          .preview-stub {
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            text-align: center;
            color: #adb5bd;
            font-size: 11px;
            font-style: italic;
            letter-spacing: 0;
            text-transform: none;
            pointer-events: none;
          }
        `}</style>
        <div
          className={`page-content xrp-template-${templateSuperId}`}
          style={{ position: 'relative', width: '100%', height: 160, overflow: 'hidden', boxSizing: 'border-box' }}
        >
          {renderArea('header', headerConfig, previewContent, headerHoverModifier)}
          <div className="preview-stub">{i18n('dc.preview.page-content-stub')}</div>
          {renderArea('footer', footerConfig, previewContent, footerHoverModifier)}
        </div>
      </root.div>
    </div>
  );
}
